import { REFERRAL_CREDIT_ENABLED_SKUS, RESEARCH_ONLY_LOCATION } from '@/constants';
import { DiscountDetails, ReferralCredit, Sku } from '@/types';

/**
 * Calculates the referral credit balance for a given SKU.
 * Returns 0 if the SKU is not eligible for referral credits.
 */
export const getReferralCreditBalance = (
  sku: Sku | null,
  referralCredit: ReferralCredit | undefined,
  locationId: string | undefined,
): number => {
  const isReferralCreditEnabled = sku?.skuID && REFERRAL_CREDIT_ENABLED_SKUS.includes(sku.skuID);
  const isResearchLocation = locationId === RESEARCH_ONLY_LOCATION;

  if (!isReferralCreditEnabled || isResearchLocation) {
    return 0;
  }
  return Number(referralCredit?.balance) || 0;
};

/**
 * Calculates the discount amount based on the discount type.
 * Supports percentage-based or fixed-value discounts.
 */
const getDiscountAmount = (sku: Sku | null, discount: DiscountDetails | undefined): number => {
  const totalPrice = sku?.price?.total ?? 0;
  const discountValue = Math.abs(Number(discount?.amount) || 0);

  return discount?.type === 'percentage' ? totalPrice * discountValue : discountValue;
};

/**
 * Calculates the discounted price based on the discount type.
 * Handles promo discounts and referral discounts.
 */
export const getDiscountedPrice = (
  sku: Sku | null,
  discountType: string | undefined,
  discountAmount: number,
  marketingReferral: any,
) => {
  const totalPrice = sku?.price?.total ?? 0;
  let discountedValue = discountAmount;

  switch (discountType) {
    case 'promo':
      discountedValue = discountAmount;
      break;
    case 'referral':
      discountedValue = marketingReferral?.referralValue ?? 0;
      break;
    default:
      discountedValue = 0;
      break;
  }

  return {
    discountedPrice: Number(totalPrice) - Number(discountedValue), // Final price after applying the discount
    appliedDiscountValueFromPromoOrMarketingReferral: Number(discountedValue), // The calculated discount value from either promo or referral
  };
};

/**
 * Main function to calculate the final price and discount details.
 * Considers referral credits, promo discounts, and marketing referrals.
 */
export const calculateDiscount = ({
  sku,
  discount,
  discountType,
  marketingReferral,
  referralCredit,
  locationId,
}: {
  sku: Sku | null;
  discount: DiscountDetails | undefined;
  discountType: string | undefined;
  marketingReferral: any;
  referralCredit: ReferralCredit | undefined;
  locationId: string | undefined;
}) => {
  const referralCreditDiscount = getReferralCreditBalance(sku, referralCredit, locationId);
  const discountAmount = getDiscountAmount(sku, discount);
  const { discountedPrice, appliedDiscountValueFromPromoOrMarketingReferral } = getDiscountedPrice(
    sku,
    discountType,
    discountAmount,
    marketingReferral,
  );
  const maxDiscount = Math.max(
    Number(appliedDiscountValueFromPromoOrMarketingReferral),
    Number(referralCreditDiscount),
  );
  const isReferralCreditUsed =
    referralCredit?.balance && Number(maxDiscount) === Number(referralCreditDiscount);
  const finalPrice = isReferralCreditUsed ? (sku?.price.total ?? 0) - maxDiscount : discountedPrice;

  return {
    totalDiscount: Number(maxDiscount), // Total discount applied (either referral or promo)
    finalPrice: finalPrice < 0 ? 0 : finalPrice, // Final price after applying the discount
    usedReferralCredit: isReferralCreditUsed, // Whether referral credit was used
  };
};
